import React, { Fragment } from 'react'
import i18n from 'i18next'
import * as R from 'ramda'
import { getParentOptions } from '../../utils/QuestionHelper'

export const getEqOperatorText = (operator) => {
    switch (operator) {
        case 'OR':
            return 'one of:'
        case 'NOR':
            return 'not one of:'
        case 'AND':
            return 'exactly:'
        case 'NAND':
            return 'not exactly:'
        default:
            return 'one of:'
    }
}

export const getNumericOperatorText = (operator) => {
    switch (operator) {
        case 'Eq':
            return 'is equal to'
        case 'Gt':
            return 'is greater than'
        case 'Lt':
            return 'is less than'
        default:
            return 'undefined'
    }
}

const optionHasLogic = (pageQuestion, optionId) => (
    R.pathOr([], ['skipLogic'], pageQuestion)
        .some((sl) => sl.action.optionId === optionId)
)

const optionSkipLogic = (pageQuestion, optionId) => (
    R.pathOr([], ['skipLogic'], pageQuestion)
        .filter((sl) => sl.action.optionId === optionId)
)

export const getInterGroupOperator = (group, groupIndex) => (
    <Fragment>
        {groupIndex !== 0
            && (
                <Fragment>
                    <br />

                    <span style={{ marginLeft: 20 }}>
                        <b>{group.interGroupLogicalOperator}</b>
                    </span>

                    <br />
                </Fragment>
            )}
    </Fragment>
)

export const getChildInterGroupOperator = (group, childIndex) => (
    <Fragment>
        {childIndex !== 0
            && R.pathOr(false, ['children', childIndex, 'interGroupLogicalOperator'], group)
            && (
                <p style={{ fontWeight: 'bold' }}>
                    {group.children[childIndex]
                        .interGroupLogicalOperator}
                </p>
            )}
    </Fragment>
)

export const getDateLogicAsText = (group, index) => {
    const {
        equalityOperator, dateField,
    } = group.children[0].triggers[0].condition

    return (
        <Fragment>
            {getChildInterGroupOperator(group, index)}

            <div className="printShowIf survey-builder-option-tab-options">
                <span>
                    {i18n.t('s_question_tab_date')}
                </span>

                <span>
                    <b>{`${equalityOperator === 'Gt' ? 'after' : 'before'}`}</b>
                </span>

                <span>the</span>

                <span>
                    <b>{`${dateField === 'startDate' ? 'survey start date' : 'survey end date'}`}</b>
                </span>
            </div>
        </Fragment>
    )
}

export const renderNumericLogic = (group, index, skipLogic, question, { locale, defaultLocale }) => {
    const { metadata, questionTypeCode } = question
    const { initialLocale } = metadata

    const questionTitle = question.content.getTitleByLocale('shortTitle', locale, defaultLocale)
    const condition = R.pathOr({}, ['triggers', 0, 'condition'], skipLogic)
    const options = R.pathOr([], ['optionSet', 'options'], question)

    const { set1, value, equalityOperator } = condition
    const option = options.find(({ id }) => id === set1)

    let optionTitle

    if (questionTypeCode !== 'I') {
        optionTitle = !R.isNil(option)
            ? option.getTitle(locale, defaultLocale, initialLocale)
            : 'unknown'
    }

    const operator = getNumericOperatorText(equalityOperator)
    const title = `${questionTitle}${!R.isNil(optionTitle) ? `- ${optionTitle}` : ''}`

    return (
        <Fragment>
            {getChildInterGroupOperator(group, index)}

            <div className="printShowIf survey-builder-option-tab-options">
                <span>
                    {i18n.t('s_question_tab_part2')}
                </span>

                <span>
                    <b>
                        {title}
                    </b>
                </span>

                <span>
                    is
                </span>

                <span>
                    <b>{operator}</b>
                </span>

                <span>
                    {value}
                </span>
            </div>
        </Fragment>
    )
}

export const renderGroupLogic = (group, survey, { locale, defaultLocale }) => (
    group.children.map((child, index) => {
        const isDate = R.pathOr('', ['triggers', 0, 'condition', 'kind'], child) === 'Date'
        const isNumeric = R.pathOr('', ['triggers', '0', 'condition', 'kind'], child) === 'QuestionHasNumericResponse'

        if (isDate) {
            return getDateLogicAsText(group, index)
        }

        const [pageQuestion] = survey.pages.map((p) => R.pathOr([], ['questions'], p).find(({ id }) => id === child.getPageQuestionId())).filter((val) => !R.isNil(val))

        if (!pageQuestion) return null

        const { question, parentId } = pageQuestion
        const { metadata } = question
        const { initialLocale } = metadata

        const parentOptions = getParentOptions(parentId, survey)
        const questionTitle = question.content.getTitleByLocale('shortTitle', locale, defaultLocale)
        const options = parentOptions.concat(R.pathOr([], ['optionSet', 'options'], question))

        const isText = question.isText()

        const groupText = []
        const eqOperator = group.children[index]
            .triggers[0].interTriggerLogicalOperator

        if (isText) {
            let optionTitle
            if (options.length > 0) {
                const set1 = R.pathOr(null, ['triggers', 0, 'condition', 'set1'], child)
                const option = options.find(({ id }) => id === set1)
                if (option) {
                    optionTitle = option.getTitle(locale, defaultLocale, initialLocale)
                }
            }
            return (
                <Fragment>
                    {getChildInterGroupOperator(group, index)}
                    <div className="printShowIf survey-builder-option-tab-options">
                        <span>
                            {i18n.t('s_question_tab_part2_text')}
                        </span>

                        <span>
                            <b>
                                {questionTitle}
                                {!R.isNil(optionTitle) ? ` - ${optionTitle}` : ''}
                            </b>
                        </span>

                        <span>
                            is
                        </span>

                        <span>
                            <b>answered</b>
                        </span>
                    </div>
                </Fragment>
            )
        }
        if (isNumeric) return renderNumericLogic(group, index, child, question, { locale, defaultLocale })

        if (options.length > 0 && !question.isDoubleAxis()) {
            group.children[index].triggers.map((trigger) => {
                const option = options.find((opt) => opt
                    .id === trigger.condition.set1)

                if (!option) {
                    return
                }

                groupText.push(option.getTitle(locale, defaultLocale, initialLocale))
            })

            const operator = getEqOperatorText(eqOperator)

            return (
                <Fragment>
                    {getChildInterGroupOperator(group, index)}

                    <div className="printShowIf survey-builder-option-tab-options">
                        <span>
                            {i18n.t('s_question_tab_part2')}
                        </span>

                        <span>
                            <b>{questionTitle}</b>
                        </span>

                        <span>
                            is
                        </span>

                        <span>
                            <b>{operator}</b>
                        </span>

                        {groupText.map((o, ind) => (
                            <Fragment>
                                {ind > 0 && <span>{eqOperator === 'AND' ? 'and' : 'or'}</span>}

                                <span>
                                    <b>{o}</b>
                                </span>
                            </Fragment>
                        ))}
                    </div>
                </Fragment>
            )
        }
        group.children[index].triggers.map((trigger) => {
            const xAxis = R.pathOr([], ['optionSetX', 'options'], question)
                .find((opt) => opt.id === trigger.condition.set2)
            const yAxis = parentOptions.concat(R.pathOr([], ['optionSetY', 'options'], question))
                .find((opt) => opt.id === trigger.condition.set1)

            if (!xAxis || !yAxis) return

            groupText.push(`${yAxis.getTitle(locale, defaultLocale, initialLocale)} - ${xAxis.getTitle(locale, defaultLocale, initialLocale)}`)
        })

        return (
            <Fragment>
                {getChildInterGroupOperator(group, index)}

                <div className="printShowIf survey-builder-option-tab-options">
                    <span>
                        {i18n.t('s_question_tab_part2')}
                    </span>

                    <span>
                        <b>{questionTitle}</b>
                    </span>

                    <span>
                        is
                    </span>

                    <span>
                        <b>{eqOperator === 'OR' ? 'One of:' : 'Not one of:'}</b>
                    </span>

                    <div>
                        {groupText.join(', ')}
                    </div>
                </div>
            </Fragment>
        )
    })
)

export const renderSkipLogicAsText = (skipLogic, survey, locales) => skipLogic.map((l) => {
    const triggerGroups = R.pathOr([], ['triggerGroups'], l)

    return triggerGroups.map((group, index) => (
        <Fragment>
            {getInterGroupOperator(group, index)}

            {renderGroupLogic(group, survey, locales)}
        </Fragment>
    ))
})

export const showPage = (survey, page, locales) => {
    const { skipLogic } = survey

    const logic = skipLogic.filter((sl) => sl.action.kind === 'ShowPage' && sl.action.pageId === page.id)

    return (
        <div>
            <span>
                {i18n.t('s_page_tab_show')}
            </span>

            <br />

            {renderSkipLogicAsText(logic, survey, locales)}
        </div>
    )
}

export const getOptions = (pageQuestion, survey, skipLogic) => {
    const { question, parentId } = pageQuestion
    const parentOptions = getParentOptions(parentId, survey)
    const getSet1 = R.pathOr(null, ['triggerGroups', 0, 'children', 0, 'triggers', 0, 'condition', 'set1'])
    const getSet2 = R.pathOr(null, ['triggerGroups', 0, 'children', 0, 'triggers', 0, 'condition', 'set2'])

    if (question.isDoubleAxis()) {
        return [
            parentOptions.concat(question.optionSetY.options).find((o) => o.id === getSet1(skipLogic)),
            question.optionSetX.options.find((o) => o.id === getSet2(skipLogic)),
        ]
    }

    return [
        parentOptions.concat(question.optionSet.options).find((o) => o.id === getSet1(skipLogic)),
    ]
}

export const pageToPage = (survey, page, { locale, defaultLocale }) => {
    const { skipLogic } = survey
    const pageQuestions = R.pathOr([], ['questions'], page).reduce((map, pageQuestion) => (
        {
            [pageQuestion.id]: pageQuestion,
            ...map,
        }
    ), {})

    const pageToPageLogic = skipLogic.filter((sl) => (
        (
            sl.action.kind === 'PageToPage'
                && R.pathOr(null, ['trigger', 'condition', 'kind'], sl) === 'CurrentPage'
                && R.pathOr(null, ['trigger', 'condition', 'pageId'], sl) === page.id
        )
    ))

    const skipToPageLogic = skipLogic.filter((sl) => (
        (
            sl.action.kind === 'SkipToPage'
                && Object.hasOwn(pageQuestions, sl.getPageQuestionId())
        )
    ))

    return (
        <div>
            {[...skipToPageLogic, ...pageToPageLogic].map((l) => {
                const { action } = l
                const { kind } = action
                const targetPage = survey.pages.find(({ id }) => id === l.action.pageId)

                const part0 = kind === 'PageToPage' ? 's_page_tab_part0' : 's_page_tab_part1'
                let part1

                if (kind === 'PageToPage') {
                    part1 = `Page ${page.priority} - ${page.title}`
                } else {
                    const pageQuestion = pageQuestions[l.getPageQuestionId()]
                    const { question } = pageQuestion
                    const { metadata } = question
                    const { initialLocale } = metadata
                    const [yOptions, xOptions] = getOptions(pageQuestion, survey, l)

                    if (!yOptions) return null

                    if (!xOptions) {
                        part1 = yOptions.getTitle(locale, defaultLocale, initialLocale)
                    } else {
                        part1 = `${yOptions.getTitle(locale, defaultLocale, initialLocale)} - ${xOptions.getTitle(locale, defaultLocale, initialLocale)}`
                    }
                }

                return (
                    <Fragment key={l.id}>
                        <div className="printShowIf survey-builder-option-tab-options">
                            <span>
                                {i18n.t(part0)}
                            </span>

                            <span>
                                <b>{part1}</b>
                            </span>

                            <span>
                                {i18n.t('s_page_tab_part2')}
                            </span>

                            <span>
                                <b>{`Page ${targetPage.priority} - ${targetPage.title}`}</b>
                            </span>
                        </div>

                        <br />
                    </Fragment>
                )
            })}
        </div>
    )
}

export const skipToPage = (survey, pageQuestion, { locale, defaultLocale }) => {
    const { skipLogic } = survey
    const { question } = pageQuestion
    const { metadata } = question
    const { initialLocale } = metadata

    const logic = skipLogic.filter((sl) => (sl.action.kind === 'SkipToPage' && sl.getPageQuestionId() === R.pathOr('', ['id'], pageQuestion)))

    return (
        <div>
            {logic.map((l) => {
                const [yOptions, xOptions] = getOptions(pageQuestion, survey, l)
                const page = survey.pages.find(({ id }) => id === l.action.pageId)

                if (!yOptions) return null

                const title = !xOptions
                    ? yOptions.getTitle(locale, defaultLocale, initialLocale)
                    : `${yOptions.getTitle(locale, defaultLocale, initialLocale)} - ${xOptions.getTitle(locale, defaultLocale, initialLocale)}`

                return (
                    <Fragment>
                        <div className="printShowIf survey-builder-option-tab-options">
                            <span>
                                {i18n.t('s_page_tab_part1')}
                            </span>

                            <span>
                                <b>{title}</b>
                            </span>

                            <span>
                                {i18n.t('s_page_tab_part2')}
                            </span>

                            <span>
                                <b>{`Page ${page.priority} - ${page.title}`}</b>
                            </span>
                        </div>

                        <br />
                    </Fragment>
                )
            })}
        </div>
    )
}

export const showQuestion = (survey, pageQuestion, locales) => {
    const { skipLogic } = survey
    const pageQuestionId = R.pathOr('', ['id'], pageQuestion)
    const qLogic = skipLogic.filter((sl) => (sl.action.kind === 'ShowQuestion' && sl.getPageQuestionId(sl) === pageQuestionId))

    return (
        <div>
            <span>
                {i18n.t('s_question_tab_part1')}
            </span>
            <br />
            <br />

            {renderSkipLogicAsText(qLogic, survey, locales)}
        </div>
    )
}

export const showOption = (survey, pageQuestion, { locale, defaultLocale }) => {
    const { question, parentId } = pageQuestion
    const { metadata } = question
    const { initialLocale } = metadata

    const parentOptions = getParentOptions(parentId, survey)
    const options = R.pathOr([], ['optionSet', 'options'], question)
    const yOptions = R.pathOr([], ['optionSetY', 'options'], question)
    const xOptions = R.pathOr([], ['optionSetX', 'options'], question)

    const optionList = [...parentOptions, ...options, ...yOptions, ...xOptions].filter(
        (o) => optionHasLogic(pageQuestion, o.id),
    )

    return (
        <div>
            {optionList.map((o) => {
                const qLogic = optionSkipLogic(pageQuestion, o.id)

                return (
                    <div>
                        <div className="printShowIf survey-builder-option-tab-options">
                            <span>{i18n.t('s_answer_option')}</span>

                            <span>
                                <b>{o.getTitle(locale, defaultLocale, initialLocale)}</b>
                            </span>

                            <span>will show:</span>
                        </div>

                        <br />

                        {renderSkipLogicAsText(qLogic, survey, { locale, defaultLocale })}

                    </div>
                )
            })}
        </div>
    )
}
